import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import InfoHeader from "../components/InfoHeader";
import { CardsPlanoDeAcao } from "../components/CardsPlanoDeAcao";
import TabelaPlanoDeAcao from "../components/TabelaPlanoDeAcao";
import { planoAcaoService } from "../services/PlanoAcaoService";
import SelectColaborador from "../components/SelectColaborador";
import SelectEstrategia from "../components/SelectEstrategia";
import SelectStatus from "../components/SelectStatus";
import PlanoAcaoResultadoConsulta from "../types/negocio/PlanoAcaoResultadoConsulta";
import PlanoAcaoModal from "../components/modais/PlanoAcaoModal";
import PlanoAcao from "../types/negocio/PlanoAcao";
import MetaGlobal from "../types/negocio/MetaGlobal";
import { metaGlobalService } from "../services/MetaGlobalService";
import { colaboradorService } from "../services/ColaboradorService";
import { PrimaryButton } from "../components";
import ProtecaoBotao from "../components/ProtecaoBotao";

export function PlanoDeAcao() {
  const [estrategia, setEstrategia] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [prazo, setPrazo] = useState("");
  const [grandeArea, setGrandeArea] = useState("");
  const [status, setStatus] = useState("");
  const [visualizacao, setVisualizacao] = useState<"tabela" | "cards">("tabela");
  const [verArquivados, setVerArquivados] = useState(false);
  const [termo, setTermo] = useState("");
  const [planos, setPlanos] = useState<PlanoAcaoResultadoConsulta[]>([]);
  const [loading, setLoading] = useState(false);

  // modal
  const [modalAberto, setModalAberto] = useState(false);
  const [planoSelecionado, setPlanoSelecionado] = useState<PlanoAcao | null>(null);

  // dados auxiliares
  const [metasGlobais, setMetasGlobais] = useState<MetaGlobal[]>([]);
  const [colaboradores, setColaboradores] = useState<any[]>([]);

  // carregar combos
  useEffect(() => {
    Promise.all([
      metaGlobalService.listarTodos(),
      colaboradorService.listarTodos(),
    ]).then(([metas, cols]) => {
      setMetasGlobais(metas);
      setColaboradores(cols);
    });
  }, []);

  const buscarPlanos = async () => {
    try {
      setLoading(true);
      const planosData = await planoAcaoService.buscarPlanos({
        estrategia: estrategia || undefined,
        responsavel: responsavel || undefined,
        prazo: prazo || undefined,
        area: grandeArea || undefined,
        status: status || undefined,
        termo: termo || undefined,
        verArquivados,
      });
      setPlanos(planosData);
    } catch (error) {
      console.error("Erro ao buscar planos de ação:", error);
      toast.error("Erro ao buscar planos de ação");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buscarPlanos();
  }, []);

  useEffect(() => {
    if (status) {
      const t = setTimeout(() => {
        buscarPlanos();
      }, 150);
      return () => clearTimeout(t);
    }
  }, [status]);

  const handleArquivarPlano = async (guid: string) => {
    try {
      await planoAcaoService.arquivarPlano(guid);
      toast.success("Plano de ação arquivado com sucesso");
      buscarPlanos();
    } catch (error) {
      console.error("Erro ao arquivar plano de ação:", error);
      toast.error("Erro ao arquivar plano de ação");
    }
  };

  const handleNovoPlano = () => {
    setPlanoSelecionado({
      guid: crypto.randomUUID(),
      fatorExternoGuid: "",
      fatorInternoGuid: "",
      metaGlobalGuid: "",
      acao: "",
      responsavelGuid: "",
      dataInicio: new Date(),
      prazoConclusao: new Date(),
      status: "",
      area: "",
      estrategia: "",
    });
    setModalAberto(true);
  };

  const handleEditarPlano = (guid: string) => {
    const plano = planos.find((p) => p.guid === guid) as PlanoAcao | undefined;
    if (plano) {
      const planoComDataCorrigida = {
        ...plano,
        dataInicio: plano.dataInicio ? new Date(plano.dataInicio) : new Date(),
        prazoConclusao: plano.prazoConclusao ? new Date(plano.prazoConclusao) : new Date()
      };
      setPlanoSelecionado(planoComDataCorrigida);
      setModalAberto(true);
    }
  };

  const handleSalvarPlano = async (plano: PlanoAcao) => {
    setModalAberto(false);
    buscarPlanos();
  };

  return (
    <Box>
      <InfoHeader
        title="Planos de Ação"
        message="Gerencie, cadastre e edite os planos de ação da sua empresa."
        isImportant
        url="https://youtu.be/72AC7euLxYY"
        modalTitle="Entenda a aba Plano de Ação"
      />

      <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
        <Box sx={{ display: "flex", gap: 2, width: "100%", flexDirection: "column" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 1, color: (theme) => theme.palette.primary.main }}
            >
              Filtros
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                id="pesquisar"
                label="Pesquisar"
                variant="outlined"
                value={termo}
                onChange={(e) => setTermo(e.target.value)}
                sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 'auto' } }}
              />

              <FormControl sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                <SelectEstrategia value={estrategia} onChange={setEstrategia} label="Estratégia" />
              </FormControl>

              <Box sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                <SelectColaborador
                  value={responsavel}
                  onChange={(colaborador) => setResponsavel(colaborador?.guid ?? "")}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flexDirection: { xs: 'column', sm: 'row' } }}>
            <FormControl sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <InputLabel id="prazo-label">Prazo</InputLabel>
              <Select
                labelId="prazo-label"
                id="prazo"
                value={prazo}
                label="Prazo"
                onChange={(e) => setPrazo(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="semana">Esta semana</MenuItem>
                <MenuItem value="mes">Este mês</MenuItem>
                <MenuItem value="semestre">Este semestre</MenuItem>
                <MenuItem value="ano">Este ano</MenuItem>
                <MenuItem value="anos">Próximos 3 anos</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <InputLabel id="area-label">Grande Área</InputLabel>
              <Select
                labelId="area-label"
                id="area"
                value={grandeArea}
                label="Grande Área"
                onChange={(e) => setGrandeArea(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="vendas">Vendas</MenuItem>
                <MenuItem value="financas">Finanças</MenuItem>
                <MenuItem value="processos/producao">Processos/Produção</MenuItem>
                <MenuItem value="pessoas">Pessoas</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <SelectStatus value={status} onChange={setStatus} label="Status" />
            </Box>

            <FormControl sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <InputLabel id="visualizacao-label">Visualização</InputLabel>
              <Select
                labelId="visualizacao-label"
                id="visualizacao"
                value={visualizacao}
                label="Visualização"
                onChange={(e) => setVisualizacao(e.target.value as "tabela" | "cards")}
              >
                <MenuItem value="tabela">Tabela</MenuItem>
                <MenuItem value="cards">Cards</MenuItem>
              </Select>
            </FormControl>

            <PrimaryButton
              variant="contained"
              color="primary"
              onClick={buscarPlanos}
              sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: '120px' }, height: "56px" }}
            >
              Filtrar
            </PrimaryButton>
          </Box>
          <FormControlLabel
            control={<Checkbox checked={verArquivados} />}
            label="Exibir planos arquivados"
            onChange={() => setVerArquivados((prev) => !prev)}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <ProtecaoBotao tela="plano-de-acao" tipo="alteracao">
            <PrimaryButton variant="contained" onClick={handleNovoPlano}>
              + Adicionar
            </PrimaryButton>
          </ProtecaoBotao>
        </Box>

        {/* Lista */}
        {visualizacao === "tabela" ? (
          <TabelaPlanoDeAcao
            planos={planos}
            loading={loading}
            onArquivar={handleArquivarPlano}
            onEditar={handleEditarPlano}
          />
        ) : (
          <CardsPlanoDeAcao
            planos={planos}
            loading={loading}
            onArquivar={handleArquivarPlano}
            onEditar={handleEditarPlano}
          />
        )}
      </Box>

      {/* Modal */}
      {planoSelecionado && (
        <PlanoAcaoModal
          open={modalAberto}
          onClose={() => setModalAberto(false)}
          onSave={handleSalvarPlano}
          plano={planoSelecionado}
          setPlano={setPlanoSelecionado as React.Dispatch<React.SetStateAction<PlanoAcao>>}
          metasGlobais={metasGlobais}
          colaboradores={colaboradores}
          modoEdicao={planos.some((p) => p.guid === planoSelecionado.guid)}
          planosExistentes={planos}
        />
      )}
    </Box>
  );
}

export default PlanoDeAcao;